import React from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import type { AssetConflictChoice, AssetFieldConflict } from '@trackly/shared';
import {
  useGetAssetCategoriesQuery,
  useGetAssetStatusesQuery,
  useGetCentersQuery,
} from '../../../../../services/DimensionService/dimensionApi';

export interface AssetConflictState {
  conflicts: AssetFieldConflict[];
  choices: Record<string, AssetConflictChoice>;
}

interface AssetConflictDialogProps {
  conflict: AssetConflictState;
  onChoiceChange: (field: string, choice: AssetConflictChoice) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isResolving: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  assetTag: 'Asset Tag',
  description: 'Description',
  serialNumber: 'Serial Number',
  manufacturer: 'Manufacturer',
  model: 'Model',
  category: 'Category',
  center: 'Center',
  building: 'Building',
  floor: 'Floor',
  room: 'Room',
  status: 'Status',
  installDate: 'Install Date',
  warrantyExpiration: 'Warranty Expiration',
  expectedLifespan: 'Expected Lifespan',
  notes: 'Notes',
};

interface LookupStatus {
  isLoading: boolean;
  retry: (() => void) | null;
}

// Coded fields arrive as codes; show the names the form's own dropdowns show. While a lookup is
// loading or failed the code itself is shown, so the dialog stays usable.
function useValueLabel(): { valueLabel: (field: string, value: string) => string; status: LookupStatus } {
  const categories = useGetAssetCategoriesQuery();
  const statuses = useGetAssetStatusesQuery();
  const centers = useGetCentersQuery();
  const queries = [categories, statuses, centers];
  const lookups: Record<string, { value: string; name: string }[]> = {
    category: categories.data ?? [],
    status: statuses.data ?? [],
    center: centers.data ?? [],
  };
  const failed = queries.filter(query => query.isError);

  return {
    valueLabel: (field, value) => {
      if (value === '') return '(empty)';
      return lookups[field]?.find(option => option.value === value)?.name ?? value;
    },
    status: {
      isLoading: queries.some(query => query.isLoading),
      retry: failed.length > 0 ? () => failed.forEach(query => query.refetch()) : null,
    },
  };
}

const LookupStatusNotice: React.FC<{ status: LookupStatus }> = ({ status }) => {
  if (status.retry) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={status.retry}>
            Retry
          </Button>
        }
      >
        Some field names could not be loaded, so codes are shown for those values.
      </Alert>
    );
  }
  if (!status.isLoading) return null;

  return (
    <Typography variant="caption" component="div" sx={{ color: '#64748b', mb: 2 }}>
      Loading field names...
    </Typography>
  );
};
// An overlapping edit: someone else saved a different value for a field the user also changed.
// Extracted from SaveAssetButton so that file stays within the Code Health gate; mirrors the
// ticket ConflictResolutionDialog (spec 010).
export const AssetConflictDialog: React.FC<AssetConflictDialogProps> = ({
  conflict,
  onChoiceChange,
  onCancel,
  onConfirm,
  isResolving,
}) => {
  const { valueLabel, status } = useValueLabel();

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onCancel}>
      <DialogTitle sx={{ borderBottom: '2px solid #3b82f6', pb: 1.5 }}>
        <Typography component="div" variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Resolve Conflicting Changes
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Someone else changed this asset while you were editing it. Choose which value to keep for each
          field below. Your other changes are merged automatically.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <LookupStatusNotice status={status} />
        <Stack spacing={3}>
          {conflict.conflicts.map(({ field, currentValue, draftValue }) => (
            <FormControl key={field} component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                {FIELD_LABELS[field] ?? field}
              </FormLabel>
              <RadioGroup
                value={conflict.choices[field] ?? 'mine'}
                onChange={event => onChoiceChange(field, event.target.value as AssetConflictChoice)}
              >
                <FormControlLabel
                  value="mine"
                  control={<Radio size="small" />}
                  label={`Keep mine: "${valueLabel(field, draftValue)}"`}
                />
                <FormControlLabel
                  value="theirs"
                  control={<Radio size="small" />}
                  label={`Keep theirs: "${valueLabel(field, currentValue)}"`}
                />
              </RadioGroup>
            </FormControl>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isResolving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isResolving}
          sx={{ backgroundColor: '#162346', '&:hover': { backgroundColor: '#1a2a52' }, fontWeight: 600 }}
        >
          {isResolving ? <CircularProgress size={20} /> : 'Save Resolution'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
